import React, { useState, useEffect } from 'react';
import { TournamentAccess, TournamentAccessUpdateInput } from '../../services/tournamentAccessService';

interface EditTournamentAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TournamentAccessUpdateInput) => Promise<void>;
  tournamentAccess: TournamentAccess | null;
}

const EditTournamentAccessModal: React.FC<EditTournamentAccessModalProps> = ({
  isOpen,
  onClose,
  onSave,
  tournamentAccess
}) => {
  const [formData, setFormData] = useState<TournamentAccessUpdateInput>({
    tournament_id: '',
    access_type: '',
    granted_by: null,
    granted_at: '',
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when tournamentAccess changes
  useEffect(() => {
    if (tournamentAccess) {
      setFormData({
        tournament_id: tournamentAccess.tournament_id,
        access_type: tournamentAccess.access_type,
        granted_by: tournamentAccess.granted_by,
        granted_at: tournamentAccess.granted_at,
        is_active: tournamentAccess.is_active
      });
      setErrors({}); // Clear any existing errors
    }
  }, [tournamentAccess]);

  const handleInputChange = (field: keyof TournamentAccessUpdateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tournament_id?.trim()) {
      newErrors.tournament_id = 'Tournament ID is required';
    }

    if (!formData.access_type?.trim()) {
      newErrors.access_type = 'Access type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error saving tournament access:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !tournamentAccess) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Tournament Access</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tournament Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tournament
            </label>
            <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
              {tournamentAccess.tournament_name || tournamentAccess.tournament_id}
            </div>
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>

          {/* Organisation Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
              {tournamentAccess.organisation_name || tournamentAccess.organisation_id}
            </div>
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>

          {/* Access Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Type *
            </label>
            <select
              value={formData.access_type}
              onChange={(e) => handleInputChange('access_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select access type...</option>
              <option value="viewer">Viewer</option>
              <option value="organizer">Organizer</option>
              <option value="participant">Participant</option>
            </select>
            {errors.access_type && (
              <p className="text-red-500 text-xs mt-1">{errors.access_type}</p>
            )}
          </div>

          {/* Granted By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Granted By
            </label>
            <input
              type="text"
              value={formData.granted_by || ''}
              onChange={(e) => handleInputChange('granted_by', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter grantor (optional)"
            />
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Active Status
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Saving...' : 'Update Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTournamentAccessModal;
